import React from "react"
import ContentAdd from "material-ui/svg-icons/content/add"
import { Paper, FloatingActionButton, CircularProgress, Toggle, SelectField, MenuItem } from "material-ui"
import TableActionBar from "../../components/layout/TableActionBar"
import EmployeeTable from "../../components/employee/EmployeeTable"
import AddUserDialog from "./AddUserDialog"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import AuthenticationStore from "../../stores/AuthenticationStore"
import strings from "../../localization/strings"
import { browserHistory } from "react-router"
import autobind from "class-autobind"
import * as permissionHelper from "../../helpers/permissionHelper"
import { ISelectedFilterTemplateId, EDefaultFilterValues, IUIUser, IUIUserSearch, IUserFilter, IUserFilterDocument, IEmployeeReportColumns } from "../../models"
import * as utils from "../../helpers/utils"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import * as api from "../../api"
import UsersFilter from "../../components/filter_template/employee/UserFilter"
import UsersReport from "./UsersReport"

interface IState {
	search: string
	users: IUIUser[]
	selectedUsers: IUIUser[]
	loading: boolean
	offset: number
	limit: number
	total: number
	filterByPunchDefault: boolean
	filterDirectReportsOnly: boolean
	showActiveEmployee: boolean
	openAddUserDialog: boolean
	selectedFilterTemplateId: ISelectedFilterTemplateId
	filterTemplates: IUserFilter[]
	employeeColumns: IEmployeeReportColumns
}

export default class Users extends React.Component<null, IState> {
	private searchTimeout
	constructor(props) {
		super(props)
		const storedFilterByPunchDefault = utils.getCookie("ShowByPunchDefaultForEmployeesPage")
		const storedFilterDirectReportsOnly = utils.getCookie("ShowDirectReportOnlyForEmployeesPage")
		const storedShowActiveEmployee = utils.getCookie("ShowActiveEmployee")

		const employeeColumns: IEmployeeReportColumns = localStorage.getItem("EmployeeColumns") ?
			JSON.parse(localStorage.getItem("EmployeeColumns")) :
			{
				IncludeFirstName: true,
				IncludeLastName: true,
				IncludePunchHierarchy: true,
				IncludeDefaultHierarchy: true,
				IncludeDefaultJob: AccountStore.getJobsEnabled(),
				IncludeDefaultPhase: AccountStore.getJobsEnabled() && AccountStore.getPhasesEnabled(),
				IncludeEmployeeId: true,
				IncludeClockPin: true,
				IncludePayrollHierarchy: AccountStore.getPayrollEnabled(),
				IncludePayrollPayGroup: AccountStore.getPayrollEnabled(),
				IncludeAgency: false,
				IncludeExempt: false,
				IncludeSupervisors: false,
				IncludePositions: false,
			}
		if (!AccountStore.getJobsEnabled())
			employeeColumns.IncludeDefaultJob = false
		if (!AccountStore.getJobsEnabled() || !AccountStore.getPhasesEnabled())
			employeeColumns.IncludeDefaultPhase = false
		if (!AccountStore.getPayrollEnabled()) {
			employeeColumns.IncludePayrollHierarchy = false
			employeeColumns.IncludePayrollPayGroup = false
		}

		this.state = {
			search: "",
			users: [],
			selectedUsers: [],
			loading: true,
			offset: 0,
			limit: 50,
			total: 0,
			filterByPunchDefault: storedFilterByPunchDefault === "true",
			filterDirectReportsOnly: storedFilterDirectReportsOnly === "true" || (AccountStore.getSupervisorDirectReportEnabled() && !permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees)),
			showActiveEmployee: storedShowActiveEmployee !== "false",
			openAddUserDialog: false,
			selectedFilterTemplateId: EDefaultFilterValues.NONE,
			filterTemplates: [],
			employeeColumns
		}
		autobind(this)
	}

	componentDidMount() {
		document.title = `${strings.Employees} | ${strings.Findd_Grandd_Central}`
		HierarchySettingsStore.on("global_change", this.globalFilterChangeEvent)
		this.getUsersAndFilters()
	}

	componentWillUnmount() {
		HierarchySettingsStore.removeListener("global_change", this.globalFilterChangeEvent)
	}

	globalFilterChangeEvent() {
		this.setState({ offset: 0 }, () =>
			this.getUsers()
		)
	}

	getUsersAndFilters() {
		this.setState({ loading: true }, () =>
			api.getFilters(AuthenticationStore.getLoggedInUser().Id).then(res => {
				const result = res.data as IUserFilterDocument
				const selectedFilterTemplateId = result && result.SelectedUserFilter ? result.SelectedUserFilter : EDefaultFilterValues.NONE
				const filterTemplates = result && result.UserFilters ? result.UserFilters : []
				this.setState({ filterTemplates, selectedFilterTemplateId }, () => this.getUsers())
			})
		)
	}

	getUsers() {
		this.state.selectedFilterTemplateId !== EDefaultFilterValues.CREATE_NEW &&
			this.setState({ loading: true }, () =>
				api.searchUsers(AccountStore.getCurrentAccount().Id, this.state.offset / this.state.limit, this.state.limit, this.getUserFilterData())
					.then(res => {
						const result = res.data as IUIUserSearch
						this.setState({
							loading: false,
							total: result.TotalDocumentCount,
							users: result.Documents,
							selectedUsers: []
						})
					})
					.catch(err => {
						err.response.status === 400 &&
							AppStateStore.setProgressSnackbarContent(err.response.data.message)
						this.setState({
							loading: false,
							total: 0,
							users: [],
							selectedUsers: []
						})
					})
			)
	}

	getUserFilterData(): IUserFilter {
		const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId)
		let levelIdFilter = HierarchySettingsStore.getGlobalLevelItemFilterId()
		if (levelIdFilter === "null") levelIdFilter = ""
		let userFilter: IUserFilter = selectedFilterTemplate ? selectedFilterTemplate : { ActiveUsers: this.state.showActiveEmployee } as IUserFilter
		userFilter = {
			...userFilter,
			Search: this.state.search,
			LevelIdFilter: levelIdFilter,
			FilterByDirectReports: this.state.filterDirectReportsOnly,
			FilterByPunchDefault: this.state.filterByPunchDefault
		} as IUserFilter
		return userFilter
	}

	handleTouchAdd() {
		const globalFilterItemId = HierarchySettingsStore.getGlobalLevelItemFilterId()
		const globalHierarchyDefinition = hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(globalFilterItemId)
		const allowAttachedEmployees = globalHierarchyDefinition ? globalHierarchyDefinition.EnableEmployees : false
		if (AccountStore.getExplicitEmployeeHierarchyAssignment() && !globalFilterItemId) {
			AppStateStore.setProgressSnackbarContent(strings.Cant_create_employee_with_root_level)
		}
		else if (AccountStore.getExplicitEmployeeHierarchyAssignment() && !allowAttachedEmployees) {
			AppStateStore.setProgressSnackbarContent(strings.This_hierarchy_item_cant_be_assigned)
		} else {
			this.setState({ openAddUserDialog: true })
		}
	}

	getUsersIdArray(users) {
		const usersIdArray = []
		if (users && users.length > 0) {
			users.forEach(user => usersIdArray.push(user.Id))
		}
		return usersIdArray
	}

	onRowClick(userId) {
		browserHistory.replace("/user-details/" + userId + "/" + this.getValidSubPages()[0])
	}

	getValidSubPages() {
		const subPages = ["timecards", "positions", "hierarchies", "records", "devices", "facespaces"]
		if (!AccountStore.getCurrentAccountSettings().FeatureSettings.EnableTimecards || !permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards)) {
			subPages.splice(subPages.indexOf("timecards"), 1)
		}
		if (!AccountStore.getCurrentAccountSettings().HierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned || !AccountStore.getCurrentAccountSettings().FeatureSettings.EnableHierarchy) {
			subPages.splice(subPages.indexOf("hierarchies"), 1)
		}
		if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.PunchRecords.ViewRecords)) {
			subPages.splice(subPages.indexOf("records"), 1)
		}
		if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.ViewUserDevices)) {
			subPages.splice(subPages.indexOf("devices"), 1)
		}
		return subPages
	}

	onPageClick(offset) {
		this.setState({ offset }, () =>
			this.getUsers()
		)
	}

	onLimitChange(limit) {
		this.setState({ offset: 0, limit }, () =>
			this.getUsers()
		)
	}

	onSearch(search) {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = null
		}
		this.setState({
			loading: true,
			search,
			offset: 0
		}, () => {
			this.searchTimeout = setTimeout(() => {
				this.getUsers()
			}, 880)
		})
	}

	onDelete(selectedUsers: IUIUser[]) {
		api.deleteEmployees(selectedUsers.map(i => i.Id)).then(() =>
			this.setState({
				offset: 0,
			}, () =>
				this.getUsers()
			)
		)
	}

	render() {
		return (
			<div className="page-container Users">
				<div className="page-container-parent">
					{this.state.showActiveEmployee && permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.AddEmployees) &&
						<FloatingActionButton
							data-intercom-target={"menu-Employees-Btn-Add"}
							className="fixed-add"
							style={{ top: 83, right: 40, zIndex: 998 }}
							secondary
							onTouchTap={this.handleTouchAdd}
						>
							<ContentAdd />
						</FloatingActionButton>
					}
					<Paper zDepth={1} className="page-paper">
						<TableActionBar
							rowSelectionCount={this.state.selectedUsers.length}
							singleSelectedMessage={strings.Employee_Selected}
							multipleSelectedMessage={strings.x_Employees_Selected}
							title={strings.Employees}
							onSearch={this.onSearch}
							searchText={this.state.search}
							reloadTable={() => this.setState({ offset: 0 }, () => this.getUsers())}
							customActionElement={
								<UsersReport
									total={this.state.total}
									employeeColumns={this.state.employeeColumns}
									selectedFilterTemplateId={this.state.selectedFilterTemplateId}
									getUserFilterData={this.getUserFilterData}
								/>
							}
							onDeleteClick={() =>
								AppStateStore.setConfirmDialogContent({
									title: this.state.selectedUsers.length === 1 ? strings.DELETE_EMPLOYEE : strings.DELETE_EMPLOYEES,
									content: this.state.showActiveEmployee ?
										AccountStore.getAccrualsTimeoffEnabled() ?
											strings.Deleting_or_Inactivating_an_employee_will_remove_all_future_holiday_leave :
											strings.Deleting_or_Inactivating_an_employee_will_remove_all_future_holiday :
										strings.Are_you_sure_you_want_to_delete_employee,
									confirm: () => this.onDelete(this.state.selectedUsers)
								})}
							readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.DeleteEmployees)}
							onCloseSearchClick={() => {
								this.setState({ selectedUsers: [] })
							}}
						/>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<UsersFilter
								filterTemplates={this.state.filterTemplates}
								selectedFilterTemplateId={this.state.selectedFilterTemplateId}
								handleChangeFilterTemplates={filterTemplates => this.setState({ filterTemplates })}
								handleSelectFilterTemlate={filterId =>
									this.setState({ selectedFilterTemplateId: filterId }, () => {
										api.setSelectedUserFilter(AuthenticationStore.getLoggedInUser().Id,
											(filterId === EDefaultFilterValues.NONE || filterId === EDefaultFilterValues.CREATE_NEW) ? "" : filterId as string
										)
										this.getUsers()
									})
								}
							/>
							{(this.state.selectedFilterTemplateId === EDefaultFilterValues.NONE || this.state.selectedFilterTemplateId == EDefaultFilterValues.CREATE_NEW) &&
								<div style={{ display: "flex", marginTop: -15, }}>
									<SelectField
										data-intercom-target={"menu-Employees-Select-Status"}
										floatingLabelText={strings.Active_Status}
										value={this.state.showActiveEmployee}
										onChange={(event, key, payload) => {
											localStorage.setItem("findd_ShowActiveEmployee", payload)
											this.setState({ showActiveEmployee: payload, offset: 0 }, () => this.getUsers())
										}}
										style={{ marginLeft: 35, marginRight: 35, width: 250 }}
									>
										<MenuItem value={true} primaryText={strings.Active} />
										<MenuItem value={false} primaryText={strings.Inactive} />
									</SelectField>
									<div style={{ marginRight: 35 }}>
										<Toggle
											data-intercom-target={"menu-Employees-Toggle-Default-Hierarchy"}
											toggled={this.state.filterByPunchDefault}
											onToggle={(e, value) => this.setState({ filterByPunchDefault: value, offset: 0, search: "" }, () => {
												this.getUsers()
												localStorage.setItem("findd_ShowByPunchDefaultForEmployeesPage", value)
											})}
											labelPosition="left"
											label={strings.Filter_By_Default_Hierarchy}
											style={{ maxWidth: 200, marginTop: 25 }}
										/>
										{AccountStore.getSupervisorDirectReportEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees) &&
											<Toggle
												data-intercom-target={"menu-Employees-Toggle-Direct-Reports-Only"}
												toggled={this.state.filterDirectReportsOnly}
												onToggle={(e, value) => this.setState({ filterDirectReportsOnly: value, offset: 0, search: "" }, () => {
													this.getUsers()
													localStorage.setItem("findd_ShowDirectReportOnlyForEmployeesPage", value)
												})}
												labelPosition="left"
												label={strings.Show_Direct_Reports_Only}
												style={{ maxWidth: 200 }}
											/>
										}
									</div>
								</div>
							}
						</div>
						<div style={{ padding: 20 }}>
							{this.state.loading ?
								<div className="spinner">
									<CircularProgress />
								</div> :
								<div>
									{!this.state.users.length ?
										<div>
											<h2 style={{ textAlign: "center", marginTop: 25 }}>
												{strings.No_employee_found}
											</h2>
										</div> :
										<EmployeeTable
											users={this.state.users}
											selectedUsers={this.state.selectedUsers}
											offset={this.state.offset}
											limit={this.state.limit}
											total={this.state.total}
											limitOptions={[50, 100, 200]}
											handleSelect={selectedUsers => this.setState({ selectedUsers })}
											onPageClick={this.onPageClick}
											onLimitChange={this.onLimitChange}
											onRowClick={this.onRowClick}
											readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.DeleteEmployees)}
											employeeColumns={this.state.employeeColumns}
											tableColumnSelect={[
												{ label: strings.First_Name, show: true, value: this.state.employeeColumns.IncludeFirstName, propertyName: "IncludeFirstName" },
												{ label: strings.Last_Name, show: true, value: this.state.employeeColumns.IncludeLastName, propertyName: "IncludeLastName" },
												{ label: strings.Punch_Hierarchy, show: true, value: this.state.employeeColumns.IncludePunchHierarchy, propertyName: "IncludePunchHierarchy" },
												{ label: strings.Default_Hierarchy, show: true, value: this.state.employeeColumns.IncludeDefaultHierarchy, propertyName: "IncludeDefaultHierarchy" },
												{ label: strings.Default_Job, show: AccountStore.getJobsEnabled(), value: this.state.employeeColumns.IncludeDefaultJob, propertyName: "IncludeDefaultJob" },
												{ label: strings.Default_Phase, show: AccountStore.getJobsEnabled() && AccountStore.getPhasesEnabled(), value: this.state.employeeColumns.IncludeDefaultPhase, propertyName: "IncludeDefaultPhase" },
												{ label: strings.Employee_ID, show: true, value: this.state.employeeColumns.IncludeEmployeeId, propertyName: "IncludeEmployeeId" },
												{ label: strings.Clock_PIN, show: true, value: this.state.employeeColumns.IncludeClockPin, propertyName: "IncludeClockPin" },
												{ label: strings.Payroll_Hierarchy, show: AccountStore.getPayrollEnabled(), value: this.state.employeeColumns.IncludePayrollHierarchy, propertyName: "IncludePayrollHierarchy" },
												{ label: strings.Pay_Group, show: AccountStore.getPayrollEnabled(), value: this.state.employeeColumns.IncludePayrollPayGroup, propertyName: "IncludePayrollPayGroup" },
												{ label: strings.Agency, show: true, value: this.state.employeeColumns.IncludeAgency, propertyName: "IncludeAgency" },
												{ label: strings.Exempt, show: true, value: this.state.employeeColumns.IncludeExempt, propertyName: "IncludeExempt" },
												{ label: strings.Supervisors, show: AccountStore.getSupervisorDirectReportEnabled(), value: this.state.employeeColumns.IncludeSupervisors, propertyName: "IncludeSupervisors" },
												{label:strings.Position, show: true,propertyName:"IncludePositions",value: this.state.employeeColumns.IncludePositions}
											]}
											handleChangeTableColumns={columns => {
												const employeeColumns = {} as IEmployeeReportColumns
												columns.forEach(i => {
													employeeColumns[i.propertyName] = i.value
												})
												this.setState({ employeeColumns })
												localStorage.setItem("EmployeeColumns", JSON.stringify(employeeColumns))
											}}
										/>
									}
								</div>
							}
						</div>
					</Paper>
				</div>
				{this.state.openAddUserDialog &&
					<AddUserDialog
						handleAdd={user => {
							api.addUserToAccount(AccountStore.getCurrentAccount().Id, user)
								.then(() => {
									AppStateStore.setProgressSnackbarContent(strings.Employee_created_successfully)
									AppStateStore.triggerGetFreeAccountMetricsData()
									this.setState({
										search: "",
										offset: 0,
									}, () =>
										this.getUsers()
									)
								})
								.catch(err => {
									if (err.response.data.message) {
										if (err.response.data.message === "Cannot add over 100 employees for free account.") {
											AppStateStore.setFreeAccountDataLimitUpgradeDialogContent(strings.Cannot_add_over_100_employees_for_free_account_Please_upgrade_your_account_plan_to_add_more)
											this.setState({ openAddUserDialog: false })
										}
										else {
											AppStateStore.setProgressSnackbarContent(err.response.data.message)
										}
									}
								})
						}}
						handleClose={() => this.setState({ openAddUserDialog: false })}
					/>
				}
			</div>
		)
	}
}
